import React, { useState, useRef, useEffect } from 'react';

/**
 * WORKING BETTING FLOW - Using refs for immediate state
 *
 * THE FIX: Use refs to store values immediately, don't wait for React state
 */

export default function WorkingBettingFlow() {
  // React state for UI updates
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [betAmount, setBetAmount] = useState<string>('');
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [logs, setLogs] = useState<string[]>([]);
  const [stepResults, setStepResults] = useState<any[]>([]);

  // REFS FOR IMMEDIATE VALUES - THIS IS THE FIX
  const optionRef = useRef<string | null>(null);
  const amountRef = useRef<string>('');
  const runningRef = useRef(false);

  const log = (message: string, data?: any) => {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
    const logEntry = `[${timestamp}] ${message}`;
    console.log(logEntry, data || '');
    setLogs(prev => [...prev, data ? `${logEntry} ${JSON.stringify(data)}` : logEntry]);
  };

  // STEPS NOW CHECK REFS, NOT STATE
  const steps = [
    {
      name: 'INIT',
      check: () => true,
      execute: () => {
        log('Step 1: Ready to start');
        log('Refs:', { option: optionRef.current, amount: amountRef.current });
        return { success: true, data: 'initialized' };
      }
    },
    {
      name: 'WAIT_OPTION',
      check: () => {
        // CHECK REF, NOT STATE!
        const hasOption = optionRef.current !== null;
        log(`Step 2 Check: optionRef=${optionRef.current} hasOption=${hasOption}`);
        return hasOption;
      },
      execute: () => {
        log('Step 2 SUCCESS: Option selected', { option: optionRef.current });
        return { success: true, data: optionRef.current };
      }
    },
    {
      name: 'WAIT_AMOUNT',
      check: () => {
        // CHECK REF, NOT STATE!
        const hasAmount = amountRef.current !== '' && parseFloat(amountRef.current) > 0;
        log(`Step 3 Check: amountRef=${amountRef.current} hasAmount=${hasAmount}`);
        return hasAmount;
      },
      execute: () => {
        log('Step 3 SUCCESS: Amount entered', { amount: amountRef.current });
        return { success: true, data: parseFloat(amountRef.current) };
      }
    },
    {
      name: 'CALCULATE',
      check: () => true,
      execute: () => {
        const amount = parseFloat(amountRef.current);
        const prob = optionRef.current === 'YES' ? 0.75 : 0.25;
        const k = 100; // liquidity constant
        const newProb = (prob * k + amount) / (k + amount);
        const shares = amount / prob;

        const result = {
          option: optionRef.current,
          amount,
          oldProb: (prob * 100).toFixed(1) + '%',
          newProb: (newProb * 100).toFixed(1) + '%',
          change: ((newProb - prob) * 100).toFixed(2) + '%',
          shares: shares.toFixed(2),
          payout: shares.toFixed(2)
        };

        log('Step 4 SUCCESS: Calculated', result);
        return { success: true, data: result };
      }
    },
    {
      name: 'SUBMIT',
      check: () => true,
      execute: async () => {
        const betId = `bet-${Date.now()}`;

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));

        const bet = {
          betId,
          marketId: 'test-market',
          userId: 'test-user',
          option: optionRef.current,
          amount: parseFloat(amountRef.current),
          timestamp: new Date().toISOString()
        };

        log('Step 5 SUCCESS: Bet submitted', bet);
        return { success: true, data: bet };
      }
    },
    {
      name: 'UPDATE_UI',
      check: () => true,
      execute: () => {
        const updates = {
          newBalance: 1000 - parseFloat(amountRef.current),
          position: {
            option: optionRef.current,
            shares: stepResults[3]?.data?.shares
          }
        };

        log('Step 6 SUCCESS: UI Updated', updates);
        return { success: true, data: updates };
      }
    }
  ];

  // EXECUTION ENGINE
  useEffect(() => {
    if (!runningRef.current) return;

    const interval = setInterval(async () => {
      if (currentStep >= steps.length) {
        log('✅ FLOW COMPLETE - ALL STEPS SUCCESSFUL');
        runningRef.current = false;
        setIsRunning(false);
        clearInterval(interval);
        return;
      }

      const step = steps[currentStep];
      log(`\n=== STEP ${currentStep + 1}: ${step.name} ===`);

      if (step.check()) {
        const result = await step.execute();
        setStepResults(prev => [...prev, result]);
        setCurrentStep(prev => prev + 1);
      } else {
        log(`⏳ WAITING for conditions...`);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [currentStep, isRunning]);

  const handleStart = () => {
    log('\n========== STARTING FLOW ==========');
    setCurrentStep(0);
    setStepResults([]);
    runningRef.current = true;
    setIsRunning(true);
  };

  const handleOptionSelect = (option: string) => {
    log(`USER: Selected ${option}`);

    // UPDATE REF IMMEDIATELY
    optionRef.current = option;
    log(`Ref updated immediately: ${optionRef.current}`);

    // Update state for UI
    setSelectedOption(option);
  };

  const handleAmountChange = (amount: string) => {
    log(`USER: Entered ${amount}`);

    // UPDATE REF IMMEDIATELY
    amountRef.current = amount;
    log(`Ref updated immediately: ${amountRef.current}`);

    // Update state for UI
    setBetAmount(amount);
  };

  const reset = () => {
    log('RESET: Clearing all data');
    setCurrentStep(0);
    setStepResults([]);
    setSelectedOption(null);
    setBetAmount('');
    optionRef.current = null;
    amountRef.current = '';
    runningRef.current = false;
    setIsRunning(false);
    setLogs([]);
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4 border border-green-400 p-2">
          <h1 className="text-2xl">WORKING BETTING FLOW - REFS FOR IMMEDIATE STATE</h1>
          <p className="text-sm text-gray-400">Using refs to store values immediately, not waiting for React state updates</p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {/* CONTROLS */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4 border-b border-green-400 pb-2">CONTROLS</h2>

            <button
              onClick={handleStart}
              className="w-full bg-green-400 text-black p-2 mb-4 hover:bg-green-300"
              disabled={isRunning}
            >
              {isRunning ? `⏳ STEP ${currentStep}/${steps.length}` : '🚀 START'}
            </button>

            <button
              onClick={reset}
              className="w-full bg-red-400 text-black p-2 mb-4 hover:bg-red-300"
            >
              🔄 RESET
            </button>

            <div className="mb-4">
              <label className="block mb-2 text-yellow-400">SELECT OPTION:</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleOptionSelect('YES')}
                  className={`p-2 border ${
                    selectedOption === 'YES' ? 'bg-green-400 text-black' : 'border-green-400'
                  }`}
                >
                  YES
                </button>
                <button
                  onClick={() => handleOptionSelect('NO')}
                  className={`p-2 border ${
                    selectedOption === 'NO' ? 'bg-red-400 text-black' : 'border-green-400'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-yellow-400">ENTER AMOUNT:</label>
              <input
                type="number"
                value={betAmount}
                onChange={(e) => handleAmountChange(e.target.value)}
                className="w-full bg-black border border-green-400 p-2 text-green-400"
                placeholder="1-1000"
              />
            </div>

            <div className="border-t border-green-400 pt-4">
              <h3 className="mb-2 text-yellow-400">STATE vs REFS:</h3>
              <pre className="text-xs">
STATE (for UI):
{JSON.stringify({
  selectedOption,
  betAmount
}, null, 2)}

REFS (for logic):
{JSON.stringify({
  optionRef: optionRef.current,
  amountRef: amountRef.current
}, null, 2)}
              </pre>
            </div>
          </div>

          {/* STEP STATUS */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4 border-b border-green-400 pb-2">STEP EXECUTION</h2>

            {steps.map((step, index) => {
              const isComplete = index < currentStep;
              const isCurrent = index === currentStep;
              const result = stepResults[index];

              return (
                <div
                  key={index}
                  className={`mb-2 p-2 border ${
                    isComplete ? 'border-green-400 bg-green-900' :
                    isCurrent ? 'border-yellow-400 bg-yellow-900 animate-pulse' :
                    'border-gray-600'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-bold">
                      {index + 1}. {step.name}
                    </span>
                    <span className="text-2xl">
                      {isComplete ? '✅' : isCurrent ? '⏳' : '⭕'}
                    </span>
                  </div>

                  {result && (
                    <div className="mt-2 text-xs bg-black p-2">
                      <pre>{JSON.stringify(result.data, null, 2)}</pre>
                    </div>
                  )}
                </div>
              );
            })}

            {currentStep === steps.length && (
              <div className="mt-4 p-2 border border-green-400 bg-green-900">
                <h3 className="text-green-400 text-xl">✅ COMPLETE!</h3>
                <p className="text-sm mt-2">All steps executed successfully</p>
              </div>
            )}
          </div>

          {/* LOGS */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4 border-b border-green-400 pb-2">EXECUTION LOG</h2>
            <div className="h-[600px] overflow-y-auto text-xs bg-black p-2">
              {logs.map((log, i) => (
                <div
                  key={i}
                  className={
                    log.includes('SUCCESS') ? 'text-green-400' :
                    log.includes('WAITING') ? 'text-yellow-400' :
                    log.includes('USER') ? 'text-blue-400' :
                    log.includes('===') ? 'text-white font-bold' :
                    'text-gray-400'
                  }
                >
                  {log}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* THE FIX EXPLANATION */}
        <div className="mt-4 border border-blue-400 p-4">
          <h2 className="text-xl text-blue-400 mb-2">WHY THIS WORKS:</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <h3 className="text-red-400 mb-2">❌ BROKEN (React State):</h3>
              <pre className="text-xs">
1. Click YES
2. setSelectedOption('YES') // ASYNC!
3. Check: selectedOption === null // TRUE
4. Step fails
5. React re-renders // TOO LATE
              </pre>
            </div>
            <div>
              <h3 className="text-green-400 mb-2">✅ WORKING (Refs):</h3>
              <pre className="text-xs">
1. Click YES
2. optionRef.current = 'YES' // IMMEDIATE!
3. Check: optionRef.current !== null // TRUE
4. Step succeeds
5. React re-renders // For UI only
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}